import { memo } from 'react';
import TabbedPanel, { TabDefinition } from 'toolkit/tabs/tabbedPanel';
import DailyActivity from './DailyActivity';
import HospitalizationPanel from './HospitalizationPanel';
import { useDocketSelectors } from 'redux/utils/selectors/docket-selectors';
import { useDocketDateRange } from '../docket-day-hook';
import { Card, Typography } from '@mui/material';
import { hospitalizationsSliceSelectors } from 'redux/slices/hospitalizations.slice';
import { useMobile } from '@act/utilities/mobile-context';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice';

interface ActivityFeedProps {
  guideId: string;
  isPast?: boolean;
  isToday?: boolean;
}

const ActivityFeed = memo(({ guideId }: ActivityFeedProps) => {
  const { isMobile } = useMobile();
  const { startEpochTime, endEpochTime } = useDocketDateRange();

  const interactions = useDocketSelectors.guideActivity({
    guideId,
    startEpochTime,
    endEpochTime,
  });

  // Get hospitalization data to show count in tab
  const hospitalizationData =
    hospitalizationsSliceSelectors.useSelectCurrentAndPendingHospitalizationsGrouped();

  // Count of unique members that have hospitalizations requiring work
  // (either no attempt or not yet offered)
  const hospitalizationCount = hospitalizationData
    ? Array.from(hospitalizationData.values()).filter(hospitalizations => {
        // Check if this member has any hospitalizations that need work:
        // 1. Hospitalizations with no attemptedAt (need attempt)
        // 2. Hospitalizations with attemptedAt but no offeredAt (need offer)
        return hospitalizations.some(h => !h.attemptedAt || !h.offeredAt);
      }).length
    : 0;

  const showHospitalizations =
    actingUserAppSelectors.useSelectActingUser()?.showHospitalizations;

  const tabDefinitions: TabDefinition[] = [
    ...(showHospitalizations
      ? [
          {
            key: 'hospitalizations',
            label: (
              <Typography variant="titleMedium">
                {`Hospitalizations${
                  hospitalizationCount > 0 ? ` (${hospitalizationCount})` : ''
                }`}
              </Typography>
            ),
            component: <HospitalizationPanel guideId={guideId} />,
          },
        ]
      : []),
    {
      key: 'activity',
      label: (
        <Typography variant="titleMedium">
          {`Activity${
            interactions.length > 0 ? ` (${interactions.length})` : ''
          }`}
        </Typography>
      ),
      component: <DailyActivity interactions={interactions} />,
    },
  ];

  return (
    <Card
      variant="outlined"
      sx={{
        flexShrink: 0,
        // Use dvh (dynamic viewport height) which adapts to orientation and split-screen
        height: isMobile ? 'min(70dvh, 800px)' : 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: isMobile ? 'min(400px, 50dvh)' : 'auto',
        width: '100%',
        maxWidth: '100%',
      }}
    >
      <TabbedPanel
        tabDefinitions={tabDefinitions}
        showNoChildBorders
        shouldEvenflow
      />
    </Card>
  );
});

ActivityFeed.displayName = 'ActivityFeed';

export default ActivityFeed;
